import { useMemo } from 'react';
import { useDispatch } from 'react-redux';
import {
  createColor, createColorWithOpacity,
} from '~/_shared/components/colorPicker/colorPicker.helpers';
import { TriStateRange } from '~/_shared/constants/triStateRange.enum';
import { useTranslation } from '~/_shared/utils/hooks';
import { getBoundaryGroupLowestZoomLevel } from '~/boundary/boundary.helpers';
import { useModal } from '~/modal/useModal.hook';
import { PurchaseDataPackageType } from '~/purchaseDataPackage/modal/purchaseDataPackageModal.component';
import { BoundaryRowComponent } from '~/sidebar/sidebarApps/mapTools/boundary/activeBoundary/boundaryItem/boundaryRow.component';
import { ZoomToLocationOrHiddenMessageComponent } from '~/sidebar/sidebarApps/mapTools/boundary/activeBoundary/zoomToLocationOrHiddenMessage.component';
import type { SortedBTGs } from '~/sidebar/sidebarApps/mapTools/boundary/boundaryTools.types';
import { isBoundaryTerritoryEmpty } from '~/sidebar/sidebarApps/mapTools/boundary/fill/boundaryFill.helpers';
import { useBoundaryItems } from '~/sidebar/sidebarApps/mapTools/boundary/hooks/useBoundaryItems';
import { useBTGFillData } from '~/sidebar/sidebarApps/mapTools/boundary/hooks/useBTGFillData';
import { useGetBoundaryTerritoryDisplayNames } from '~/sidebar/sidebarApps/mapTools/boundary/hooks/useGetBoundaryTerritoryDisplayName';
import { ActiveDataLayerComponent } from '~/sidebar/sidebarApps/mapTools/dataLayersTool/listing/activeDataLayer.component';
import { useIsDataLayersToolLoading } from '~/sidebar/sidebarApps/mapTools/dataLayersTool/useIsDataLayersToolLoading';
import type {
  BoundaryGroupId, BoundaryTerritoryId,
} from '~/store/boundaries/boundaryIdentifier.type';
import {
  useBoundaryGroupsSelector, useGetBoundaryGroup,
} from '~/store/boundaryGroups/boundaryGroups.selectors';
import {
  useBoundaryTerritoryAssignments,
  useBoundaryTerritoryBoundsDetailsIsLoadingSelector, useBoundaryTerritoryDetailsSelector,
} from '~/store/boundaryTerritoryDetails/boundaryTerritoryDetails.selectors';
import type { BoundaryTerritory } from '~/store/boundaryTerritoryGroups/boundaryTerritoryGroups.state';
import {
  useActivePremiumDemographicsAllSelector, useActivePremiumDemographicsPackagesSelector,
} from '~/store/demographics/premiumDemographics/demographicsListPremium.selectors';
import { useDataLayersTemplates } from '~/store/frontendState/mapTools/dataLayers/useDataLayersTemplates';
import { mapSettingsDataLayersRemoveDataLayer } from '~/store/mapSettings/data/dataLayers/mapSettingsDataLayers.actionCreators';
import type { DataLayerGroup } from '~/store/mapSettings/data/dataLayers/mapSettingsDataLayers.state';
import { setDataLayersFilter } from '~/store/mapSettings/toolsState/dataLayers/mapSettingsToolsStateDataLayers.actionCreators';
import { useDataLayersFilters } from '~/store/mapSettings/toolsState/dataLayers/mapSettingsToolsStateDataLayers.selectors';
import { useIsMapEditDisabled } from '~/store/selectors/useMapInfoSelectors';

export const ActiveDataLayerContainer = ({
  boundaryGroupId,
  boundaryTerritoryGroups,
  dataLayerGroup,
}: Readonly<{
  boundaryGroupId: BoundaryGroupId;
  boundaryTerritoryGroups: SortedBTGs;
  dataLayerGroup: DataLayerGroup;
}>) => {
  const dispatch = useDispatch();
  const [t] = useTranslation();
  const isEditModeDisabled = useIsMapEditDisabled();
  const isDataLayersToolLoading = useIsDataLayersToolLoading();
  const boundaryGroups = useBoundaryGroupsSelector();
  const { getBoundaryTerritoryDisplayName } = useGetBoundaryTerritoryDisplayNames();
  const boundaryFilters = useDataLayersFilters();
  const loadedBoundaryDetails = useBoundaryTerritoryDetailsSelector();
  const isLoadingBoundsLimitedDetails = useBoundaryTerritoryBoundsDetailsIsLoadingSelector();
  const { getBoundaryGroupById } = useGetBoundaryGroup();
  const boundaryGroup = getBoundaryGroupById(boundaryGroupId);
  const activePremiumDemographics = useActivePremiumDemographicsAllSelector();
  const accessiblePackages = useActivePremiumDemographicsPackagesSelector().accessible;
  const { createNewTemplate, updateDataLayerTemplate, deleteDataLayerTemplate } = useDataLayersTemplates();
  const boundaryTerritoryAssignments = useBoundaryTerritoryAssignments();
  const { openModal: openDataLayerEdit } = useModal('DataLayerEdit');
  const { openModal: openDataLayerFillEdit } = useModal('DataLayerFillEdit');
  const { openModal: openDataLayerStyleEdit } = useModal('DataLayerStyleEdit');
  const { openModal: openPurchaseDataPackageModal } = useModal('PurchaseDataPackage');

  const isTrialMode = accessiblePackages.size === 0;
  const filter = boundaryFilters.get(boundaryGroupId);
  const boundaryGroupName = boundaryGroups.find(bg => bg.id === boundaryGroupId)?.name;

  const hasInaccessibleDemographics = useMemo(() => (
    dataLayerGroup.data.demographics.some(d => !activePremiumDemographics.accessible.has(d))
  ), [dataLayerGroup, activePremiumDemographics]);

  const hasNonpurchasedDemographics = useMemo(() => (
    dataLayerGroup.data.demographics.some(d => !activePremiumDemographics.purchased.has(d))
  ), [dataLayerGroup, activePremiumDemographics]);

  const dropdownProps = useBTGFillData({
    boundaryGroupId,
    boundaryTerritoryGroups,
  });

  const boundaryTerritoryGroup = dropdownProps.activeBoundaryTerritoryGroup;
  const activeBoundaryTerritoryGroupDemographic = boundaryTerritoryGroup.settings.demographicId;

  const isCurrentDemographicPurchased = useMemo(() => (
    activeBoundaryTerritoryGroupDemographic
      ? activePremiumDemographics.purchased.has(activeBoundaryTerritoryGroupDemographic)
      : false
  ), [activeBoundaryTerritoryGroupDemographic, activePremiumDemographics]);

  const boundaryItems = useBoundaryItems({ boundaryTerritoryGroup });

  const handleDataLayerRemove = () => {
    dispatch(mapSettingsDataLayersRemoveDataLayer(dataLayerGroup));
  };

  const handleDataLayerEdit = () => {
    openDataLayerEdit({ dataLayerId: dataLayerGroup.id });
  };

  const handleDataLayerFillEdit = () => {
    openDataLayerFillEdit({ dataLayerGroupId: dataLayerGroup.id });
  };

  const handleDataLayerStyleEdit = () => {
    openDataLayerStyleEdit({ dataLayerGroupId: dataLayerGroup.id });
  };

  const handleSaveAsTemplate = () => {
    if (dataLayerGroup.fromTemplate) {
      updateDataLayerTemplate(dataLayerGroup.fromTemplate, dataLayerGroup);
    }
    else {
      createNewTemplate(dataLayerGroup);
    }
  };

  const handleDeleteTemplate = () => {
    if (dataLayerGroup.fromTemplate) {
      deleteDataLayerTemplate(dataLayerGroup.fromTemplate);
    }
    handleDataLayerRemove();
  };

  const onToggleShowAll = () => {
    dispatch(setDataLayersFilter(boundaryTerritoryGroup.boundaryGroupId, {
      ...filter,
      filteredBoundaryTerritories: [],
      filteredBoundaries: [],
      showAll: filter.showAll === TriStateRange.Full
        ? TriStateRange.None
        : TriStateRange.Full,
    }));
  };

  const onPurchase = () => {
    openPurchaseDataPackageModal({
      type: PurchaseDataPackageType.DataLayer,
    });
  };

  const setBoundaryTerritoriesFilter = (filteredBoundaryTerritories: BoundaryTerritoryId[], showAll: TriStateRange) => {
    dispatch(setDataLayersFilter(boundaryTerritoryGroup.boundaryGroupId, {
      ...filter,
      filteredBoundaryTerritories,
      showAll,
    }));
  };

  const getFilterBoundaryTerritoryToggleHandler = (boundaryTerritory: BoundaryTerritory) => () => {
    const newFilter = filter.filteredBoundaryTerritories
      .filter(territoryId => territoryId !== boundaryTerritory.boundaryTerritoryId);

    if (newFilter.length === filter.filteredBoundaryTerritories.length) {
      newFilter.push(boundaryTerritory.boundaryTerritoryId);
    }
    const showAll = newFilter.length ? TriStateRange.Partial : TriStateRange.None;

    setBoundaryTerritoriesFilter(newFilter, showAll);
  };

  const isRestrictedAccess = isEditModeDisabled || !isCurrentDemographicPurchased;
  const targetZoomLevel = boundaryGroup && getBoundaryGroupLowestZoomLevel(boundaryGroup, boundaryTerritoryGroup);
  const boundaryDetails = loadedBoundaryDetails.get(boundaryTerritoryGroup.boundaryTerritoryGroupId);
  const allBoundariesHidden = filter.showAll === TriStateRange.None;
  const fillRequiresZoomToLoadDetails = !isDataLayersToolLoading && !boundaryDetails && !!targetZoomLevel;

  return (
    <ActiveDataLayerComponent
      boundaryGroupName={boundaryGroupName}
      dataLayer={dataLayerGroup}
      dropdownProps={dropdownProps}
      hasInaccessibleDemographics={hasInaccessibleDemographics}
      hasNonpurchasedDemographics={hasNonpurchasedDemographics}
      isCurrentDemographicPurchased={isCurrentDemographicPurchased}
      toolbarProps={{
        boundaryDisplayName: boundaryGroupName ?? '',
        onDeleteTemplate: dataLayerGroup.fromTemplate && !isEditModeDisabled ? handleDeleteTemplate : undefined,
        onSaveAsTemplate: isTrialMode || hasNonpurchasedDemographics ? undefined : handleSaveAsTemplate,
        onRemove: isEditModeDisabled ? undefined : handleDataLayerRemove,
        onEditLayers: hasNonpurchasedDemographics || isRestrictedAccess ? undefined : handleDataLayerEdit,
        onEditFill: isTrialMode || hasNonpurchasedDemographics || isRestrictedAccess ? undefined : handleDataLayerFillEdit,
        onEditStyle: isRestrictedAccess ? undefined : handleDataLayerStyleEdit,
        onPurchase: isTrialMode || hasNonpurchasedDemographics ? onPurchase : undefined,
      }}
      showAllCheckmark={filter.showAll}
      onToggleShowAll={isRestrictedAccess ? undefined : onToggleShowAll}
    >
      {fillRequiresZoomToLoadDetails ? (
        <ZoomToLocationOrHiddenMessageComponent
          allBoundariesHidden={allBoundariesHidden}
          targetZoomLevel={targetZoomLevel}
          isLoading={isLoadingBoundsLimitedDetails}
        />
      ) : (
        <>
          {boundaryItems.displayBoundaryTerritories && boundaryItems.boundaryTerritoryList.map(boundaryTerritory => {
            const setting = {
              isVisible: filter.filteredBoundaryTerritories.includes(boundaryTerritory.boundaryTerritoryId),
              label: getBoundaryTerritoryDisplayName(boundaryTerritory.boundaryTerritoryId,
                boundaryTerritoryGroup.boundaryTerritoryGroupId),
              selectedColor: createColorWithOpacity(boundaryTerritory.style.color, boundaryTerritory.style.opacity / 100),
              lineColor: createColor(boundaryTerritoryGroup.settings.style.lineColor),
              lineWidth: boundaryTerritoryGroup.settings.style.lineWidth,
              onToggle: isRestrictedAccess ? undefined : getFilterBoundaryTerritoryToggleHandler(boundaryTerritory),
              isEmpty: isBoundaryTerritoryEmpty(boundaryTerritoryAssignments, boundaryTerritoryGroup.boundaryTerritoryGroupId, boundaryTerritory),
            };

            return (
              <BoundaryRowComponent
                key={boundaryTerritory.boundaryTerritoryId}
                isChecked={setting.isVisible}
                label={setting.label}
                subLabel={setting.isEmpty ? t('no data') : undefined}
                isDisabled={setting.isEmpty}
                onToggle={setting.onToggle}
                selectedColor={setting.selectedColor}
                lineColor={setting.lineColor}
                lineWidth={setting.lineWidth}
              />
            );
          })}
        </>
      )}
    </ActiveDataLayerComponent>
  );
};
